/**
 * Service de logging sécurisé
 * Log uniquement en développement, masque les données sensibles
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Clés considérées comme sensibles (ne doivent jamais être loggées)
 */
const SENSITIVE_KEYS = [
    'password',
    'token',
    'access_token',
    'refresh_token',
    'apiKey',
    'api_key',
    'secret',
    'authorization',
    'credit_card',
    'cvv',
    'ssn'
];

/**
 * Masque les données sensibles dans un objet
 * @param {any} data - Les données à masquer
 * @returns {any} - Les données masquées
 */
const maskSensitiveData = (data) => {
    if (!data || typeof data !== 'object') return data;

    if (Array.isArray(data)) {
        return data.map(item => maskSensitiveData(item));
    }

    const masked = { ...data };
    
    Object.keys(masked).forEach(key => {
        const lowerKey = key.toLowerCase();
        
        // Masquer les clés sensibles
        if (SENSITIVE_KEYS.some(sensitiveKey => lowerKey.includes(sensitiveKey))) {
            masked[key] = '***HIDDEN***';
        } 
        // Récursivement masquer les objets imbriqués
        else if (typeof masked[key] === 'object' && masked[key] !== null) {
            masked[key] = maskSensitiveData(masked[key]);
        }
    });

    return masked;
};

/**
 * Log de débogage (uniquement en développement)
 * @param {string} message - Le message à logger
 * @param  {...any} args - Arguments supplémentaires
 */
export const debug = (message, ...args) => {
    if (isDevelopment && console && console.log) {
        const maskedArgs = args.map(arg => maskSensitiveData(arg));
        console.log(`[DEBUG] ${message}`, ...maskedArgs);
    }
};

/**
 * Log d'information (uniquement en développement)
 * @param {string} message - Le message à logger
 * @param  {...any} args - Arguments supplémentaires
 */
export const info = (message, ...args) => {
    if (isDevelopment && console && console.info) {
        const maskedArgs = args.map(arg => maskSensitiveData(arg));
        console.info(`[INFO] ${message}`, ...maskedArgs);
    }
};

/**
 * Log d'avertissement (uniquement en développement)
 * @param {string} message - Le message à logger
 * @param  {...any} args - Arguments supplémentaires
 */
export const warn = (message, ...args) => {
    if (isDevelopment && console && console.warn) {
        const maskedArgs = args.map(arg => maskSensitiveData(arg));
        console.warn(`[WARN] ${message}`, ...maskedArgs);
    }
};

/**
 * Log d'erreur (toujours actif mais masque les données sensibles)
 * @param {string} message - Le message à logger
 * @param  {...any} args - Arguments supplémentaires
 */
export const error = (message, ...args) => {
    if (console && console.error) {
        const maskedArgs = args.map(arg => maskSensitiveData(arg));
        
        if (isDevelopment) {
            console.error(`[ERROR] ${message}`, ...maskedArgs);
        } else {
            // En production, logger uniquement le message sans détails
            console.error(`[ERROR] ${message}`);
        }
    }
};

/**
 * Log de succès (uniquement en développement)
 * @param {string} message - Le message à logger
 * @param  {...any} args - Arguments supplémentaires
 */
export const success = (message, ...args) => {
    if (isDevelopment && console && console.log) {
        const maskedArgs = args.map(arg => maskSensitiveData(arg));
        console.log(`%c[SUCCESS] ${message}`, 'color: green; font-weight: bold', ...maskedArgs);
    }
};

/**
 * Log de performance (uniquement en développement)
 * @param {string} label - Le label de la mesure
 * @param {Function} fn - La fonction à mesurer
 * @returns {any} - Le résultat de la fonction
 */
export const performance = async (label, fn) => {
    if (!isDevelopment) {
        return await fn();
    }

    const startTime = performance.now();
    try {
        const result = await fn();
        const endTime = performance.now();
        console.log(`%c[PERF] ${label}: ${(endTime - startTime).toFixed(2)}ms`, 'color: blue');
        return result;
    } catch (error) {
        const endTime = performance.now();
        console.error(`%c[PERF] ${label}: Failed after ${(endTime - startTime).toFixed(2)}ms`, 'color: red', error);
        throw error;
    }
};

/**
 * Groupe de logs (uniquement en développement)
 * @param {string} label - Le label du groupe
 * @param {Function} fn - La fonction contenant les logs
 */
export const group = (label, fn) => {
    if (isDevelopment && console && console.group) {
        console.group(label);
        try {
            fn();
        } finally {
            console.groupEnd();
        }
    }
};

/**
 * Table de données (uniquement en développement)
 * @param {Array|Object} data - Les données à afficher
 * @param {Array} columns - Les colonnes à afficher (optionnel)
 */
export const table = (data, columns) => {
    if (isDevelopment && console && console.table) {
        const maskedData = maskSensitiveData(data);
        console.table(maskedData, columns);
    }
};

/**
 * Assertion (uniquement en développement)
 * @param {boolean} condition - La condition à vérifier
 * @param {string} message - Le message si la condition est fausse
 */
export const assert = (condition, message) => {
    if (isDevelopment && console && console.assert) {
        console.assert(condition, message);
    }
};

export default {
    debug,
    info,
    warn,
    error,
    success,
    performance,
    group,
    table,
    assert,
    maskSensitiveData
};
